// Project types matching the Vue.js/Symfony constants
export const PROJECT_TYPE_NEW_LOAN = 'newLoan'
export const PROJECT_TYPE_SWITCHING = 'switching' // Hamon (< 1 year)
export const PROJECT_TYPE_BOURQUIN = 'bourquin' // Bourquin (> 1 year)

// All valid project types
export const PROJECT_TYPES = [PROJECT_TYPE_NEW_LOAN, PROJECT_TYPE_SWITCHING, PROJECT_TYPE_BOURQUIN] as const

// Signature-date bounds (parité parcours legacy Symfony, window.LOAN_OPTIONS)
export const LOAN_MAX_PERIOD_MONTHS = 480 // ~40 ans, cf. Utwin AbstractAPI::MAX_LOAN_PERIOD
export const LOAN_SIGNATURE_MAX_FUTURE_MONTHS = 12 // plafond futur (nouveau prêt / rachat)

// Valeurs de Loan::INFORMATION_TYPE_* (src/Entity/Loan/Loan.php), exposées par window.LOAN_OPTIONS.loanInformationTypes
export const LOAN_INFORMATION_TYPES = {
    SUM_LEFT: 0,
    SUM_INITIAL: 1,
} as const

export type ProjectType = (typeof PROJECT_TYPES)[number]

// Helper functions for project type checks
export const isNewLoan = (typeQuote: string | undefined | null): boolean => {
    return typeQuote === PROJECT_TYPE_NEW_LOAN || !typeQuote
}

export const isSwitching = (typeQuote: string | undefined | null): boolean => {
    return typeQuote === PROJECT_TYPE_SWITCHING
}

export const isBourquin = (typeQuote: string | undefined | null): boolean => {
    return typeQuote === PROJECT_TYPE_BOURQUIN
}

export const needsSwitching = (typeQuote: string | undefined | null): boolean => {
    return typeQuote === PROJECT_TYPE_SWITCHING || typeQuote === PROJECT_TYPE_BOURQUIN
}

// Loan status list — values resolved on the Symfony side by LoanQuote::resolveStatusValue
// (string -> STATUS_<UPPER> constant). They feed the Brevo deal stages (funnel reporting).
export const LOAN_STATUS = {
    PROJECT_STEP_VISITED: 'project_step_visited',
    QUOTE_STEP_VISITED: 'quote_step_visited',
    IDENTITY_STEP_VISITED: 'identity_step_visited',
    COLD_LEAD: 'cold_lead',
} as const

// Cookie name for storing last token
export const LAST_TOKEN_COOKIE_NAME = 'rm_loan_last_token'

// Job type ID constants (matching backend database IDs)
export const EXECUTIVE_JOB_ID = 1
export const EMPLOYEE_JOB_ID = 2
export const LIBERAL_JOB_ID = 4
export const CRAFTSMAN_JOB_ID = 5
export const FARMER_JOB_ID = 7
export const TEMPORARY_JOB_ID = 8
export const RETIRED_JOB_ID = 9
export const UNEMPLOYED_JOB_ID = 11
export const CEO_JOB_ID = 12
export const TEACHER_JOB_ID = 13
export const MEDICAL_JOB_ID = 16
export const WORKER_JOB_ID = 28
export const CEO_SMALL_JOB_ID = 29
export const SELF_EMPLOYED_JOB_ID = 30

// Jobs that may involve handwork (manutention)
export const HANDWORK_JOBS = [
    EMPLOYEE_JOB_ID,
    CRAFTSMAN_JOB_ID,
    WORKER_JOB_ID,
    LIBERAL_JOB_ID,
    FARMER_JOB_ID,
    TEMPORARY_JOB_ID,
    CEO_JOB_ID,
    CEO_SMALL_JOB_ID,
    SELF_EMPLOYED_JOB_ID,
]

// Jobs that may involve working at height
export const IN_HEIGHT_JOBS = [
    EMPLOYEE_JOB_ID,
    CRAFTSMAN_JOB_ID,
    WORKER_JOB_ID,
    LIBERAL_JOB_ID,
    FARMER_JOB_ID,
    TEMPORARY_JOB_ID,
    CEO_JOB_ID,
    CEO_SMALL_JOB_ID,
    SELF_EMPLOYED_JOB_ID,
]

// Jobs excluded from retired previous job list
export const RETIRED_EXCLUDED_JOBS = [RETIRED_JOB_ID, UNEMPLOYED_JOB_ID]

// =============================================================================
// PROJECT PURPOSE (Type de projet)
// Maps to TypeInsurance entity IDs in Symfony (same as Vue)
// =============================================================================
export const PROJECT_PURPOSE_RENTAL = 1 // Investissement locatif
export const PROJECT_PURPOSE_RESIDENTIAL = 2 // Achat résidence principale / secondaire
export const PROJECT_PURPOSE_PROFESSIONAL = 3 // Prêt professionnel
export const PROJECT_PURPOSE_SCPI = 4 // SCPI

// Secondary homes share the residential TypeInsurance: the split is a wording choice, not a new backend type
export const PROJECT_PURPOSES = [
    {
        key: 'residential',
        id: PROJECT_PURPOSE_RESIDENTIAL,
        label: 'Résidence principale',
        icon: 'house',
    },
    {
        key: 'residential-secondary',
        id: PROJECT_PURPOSE_RESIDENTIAL,
        label: 'Résidence secondaire',
        icon: 'umbrella-beach',
    },
    {
        key: 'rental',
        id: PROJECT_PURPOSE_RENTAL,
        label: 'Investissement locatif',
        icon: 'building',
    },
    {
        key: 'professional',
        id: PROJECT_PURPOSE_PROFESSIONAL,
        label: 'Prêt professionnel',
        icon: 'briefcase',
    },
    {
        key: 'scpi',
        id: PROJECT_PURPOSE_SCPI,
        label: 'SCPI',
        icon: 'chart-line',
    },
] as const

export type ProjectPurposeKey = (typeof PROJECT_PURPOSES)[number]['key']

// AbstractAPI::SPORT_AUCUN
export const SPORT_NONE_VALUE = 8

// Tooltips for project purposes (currently not used, prepared for future)
// export const PROJECT_PURPOSE_TOOLTIPS: Partial<Record<number, string>> = {}

// =============================================================================
// GUARANTEES (Garanties)
// =============================================================================
export const GUARANTEE_DC = 'DC'
export const GUARANTEE_PTIA = 'PTIA'
export const GUARANTEE_IPT_ITT = 'IPT_ITT'
export const GUARANTEE_IPP = 'IPP'
export const GUARANTEE_PLUS = 'PLUS' // Dos/Psy

export const GUARANTEES = [GUARANTEE_DC, GUARANTEE_PTIA, GUARANTEE_IPT_ITT, GUARANTEE_IPP, GUARANTEE_PLUS] as const

export type GuaranteeCode = (typeof GUARANTEES)[number]

export const GUARANTEE_PILL_LABELS: Record<GuaranteeCode, string> = {
    [GUARANTEE_DC]: 'Décès',
    [GUARANTEE_PTIA]: 'PTIA',
    [GUARANTEE_IPT_ITT]: 'ITT/IPT',
    [GUARANTEE_PLUS]: 'Dos & Psy',
    [GUARANTEE_IPP]: 'IPP',
}

// Ordre d'affichage des pills « Garanties Incluses » sur les cartes de résultats (maquette).
export const GUARANTEE_PILL_ORDER: GuaranteeCode[] = [
    GUARANTEE_DC,
    GUARANTEE_PTIA,
    GUARANTEE_IPT_ITT,
    GUARANTEE_PLUS,
    GUARANTEE_IPP,
]

// Guarantee conditions structure (same as Symfony Bank.php GUARANTEES_AVAILABLE_CONDITIONS)
export interface GuaranteeConditions {
    oldBorrower: boolean // Available for old borrowers (65+)
    retired: boolean // Available for retired people
    unemployed: boolean // Available for unemployed people
    expat: boolean // Available for expatriates
    requiredForGuarantees: GuaranteeCode[] // Guarantees that depend on this one
    dependingOnGuarantees: GuaranteeCode[] // Guarantees this one depends on
}

// Guarantee data with descriptions and icons - matches Symfony Bank.php GUARANTEES_DATA
export const GUARANTEES_DATA: Record<
    GuaranteeCode,
    {
        name: string
        code: GuaranteeCode
        htmlDescription: string
        icon: string
        uncheckableIfRequired: boolean
        conditions: GuaranteeConditions
    }
> = {
    [GUARANTEE_DC]: {
        name: 'Décès',
        code: GUARANTEE_DC,
        htmlDescription:
            "La garantie décès vous assure en cas de décès, le capital restant dû est remboursé à l'organisme prêteur en sa totalité selon la quotité choisie",
        icon: 'bed',
        uncheckableIfRequired: false,
        conditions: {
            oldBorrower: true, // Available for all
            retired: true, // Available for retired
            unemployed: true, // Available for unemployed
            expat: true, // Available for expats
            requiredForGuarantees: [],
            dependingOnGuarantees: [],
        },
    },
    [GUARANTEE_PTIA]: {
        name: "Perte d'autonomie (PTIA)",
        code: GUARANTEE_PTIA,
        htmlDescription:
            "<strong>PTIA (Perte Totale et Irréversible d'Autonomie) :</strong> Dans le cas d'un handicap extrême, l'assurance rembourse le capital restant dû (à hauteur de la quotité choisie) si l'assuré n'est plus en capacité d'effectuer les gestes les plus élémentaires de la vie courante.",
        icon: 'wheelchair',
        uncheckableIfRequired: false,
        conditions: {
            oldBorrower: false, // NOT available for 65+
            retired: true, // Available for retired
            unemployed: true, // Available for unemployed
            expat: true, // Available for expats
            requiredForGuarantees: [],
            dependingOnGuarantees: [],
        },
    },
    [GUARANTEE_IPT_ITT]: {
        name: 'Incapacité temporaire de travail (ITT) & Invalidité permanente totale (IPT)',
        code: GUARANTEE_IPT_ITT,
        htmlDescription:
            "<strong>Invalidité Permanente Totale (IPT) :</strong> Si votre taux d'invalidité permanente après une <u>maladie ou un accident</u> est supérieur ou égal à 66% (Taux déterminé par un médecin) et vous empêche d'exercer votre profession, l'assurance remboursera la totalité du capital restant dû.<br><br><strong>Incapacité de Travail Temporaire (ITT) :</strong> Lorsque <u>l'arrêt de travail</u> est de plus de 90 jours continus, l'assureur remboursera les échéances mensuelles à votre organisme prêteur.",
        icon: 'wheelchair',
        uncheckableIfRequired: true,
        conditions: {
            oldBorrower: false, // NOT available for 65+
            retired: true, // Available for retired (insurers' age limits apply)
            unemployed: false, // NOT available for unemployed
            expat: false, // NOT available for expats (only in specific countries)
            requiredForGuarantees: [GUARANTEE_IPP, GUARANTEE_PLUS],
            dependingOnGuarantees: [],
        },
    },
    [GUARANTEE_IPP]: {
        name: 'Invalidité permanente partielle (IPP)',
        code: GUARANTEE_IPP,
        htmlDescription:
            "<strong>Invalidité Permanente Partielle (IPP) :</strong> Si votre taux d'invalidité permanente après une <u>maladie ou un accident</u> est compris entre 33% et 66% (Taux déterminé par un médecin) et vous empêche d'exercer votre profession, l'assurance remboursera la totalité du capital restant dû.<br>Cette garantie est complémentaire à l'IPT",
        icon: 'crutch',
        uncheckableIfRequired: true,
        conditions: {
            oldBorrower: false, // NOT available for 65+
            retired: true, // Available for retired (insurers' age limits apply)
            unemployed: false, // NOT available for unemployed
            expat: false, // NOT available for expats (only in specific countries)
            requiredForGuarantees: [],
            dependingOnGuarantees: [GUARANTEE_IPT_ITT],
        },
    },
    [GUARANTEE_PLUS]: {
        name: 'Dos & Psy',
        code: GUARANTEE_PLUS,
        htmlDescription:
            "Avec cette option vous serez couvert par l'assureur en cas d'arrêt de travail (de plus de 90 jours) <strong>lié au dos</strong> (lombalgie, hernie discale ...) ou à <strong>l'aspect psychologique</strong> (dépression, burn out ...).<br>Ainsi, l'assureur prendra en charge les mensualités de votre prêt durant un arrêt de travail de plus de 90 jours.",
        icon: 'band-aid',
        uncheckableIfRequired: true,
        conditions: {
            oldBorrower: false, // NOT available for 65+
            retired: true, // Available for retired (insurers' age limits apply)
            unemployed: false, // NOT available for unemployed
            expat: false, // NOT available for expats (only in specific countries)
            requiredForGuarantees: [],
            dependingOnGuarantees: [GUARANTEE_IPT_ITT],
        },
    },
}

// Labels affichés côté front : priment sur les names servis par l'API (globalOptions.bankGuaranteesData)
export const GUARANTEE_LABELS: Record<GuaranteeCode, string> = {
    [GUARANTEE_DC]: 'Décès',
    [GUARANTEE_PTIA]: "Perte d'autonomie (PTIA)",
    [GUARANTEE_IPT_ITT]: 'Incapacité temporaire de travail (ITT) & Invalidité permanente totale (IPT)',
    [GUARANTEE_IPP]: 'Invalidité permanente partielle (IPP)',
    [GUARANTEE_PLUS]: 'Dos & Psy',
}

// Ordre d'affichage des cartes garanties (maquette : Dos & Psy avant IPP)
export const GUARANTEE_DISPLAY_ORDER: GuaranteeCode[] = [
    GUARANTEE_DC,
    GUARANTEE_PTIA,
    GUARANTEE_IPT_ITT,
    GUARANTEE_PLUS,
    GUARANTEE_IPP,
]

// Short plain-text descriptions shown under each guarantee card
export const GUARANTEE_SHORT_DESCRIPTIONS: Record<GuaranteeCode, string> = {
    [GUARANTEE_DC]:
        "En cas de décès, le capital restant dû est intégralement remboursé à la banque — vos proches n'héritent pas de la dette.",
    [GUARANTEE_PTIA]:
        "Si une maladie ou un accident vous empêche définitivement d'accomplir seul les actes essentiels du quotidien, le prêt est soldé à votre place.",
    [GUARANTEE_IPT_ITT]:
        "Vos mensualités sont prises en charge pendant un arrêt de travail (ITT) ou si une invalidité grave vous empêche définitivement d'exercer toute activité professionnelle (IPT).",
    [GUARANTEE_IPP]:
        'Couvre une invalidité partielle reconnue entre 33 % et 66 %, lorsque vous pouvez encore travailler mais avec des capacités réduites.',
    [GUARANTEE_PLUS]:
        'Étend votre couverture ITT aux pathologies dorsales (hernies, lombalgies…) et aux troubles psychiatriques, habituellement exclus des contrats standards.',
}

// Compact funnel cards: short title and one-line explanation, the full text moves to the info tooltip
export const GUARANTEE_COMPACT_LABELS: Record<GuaranteeCode, string> = {
    [GUARANTEE_DC]: 'Décès',
    [GUARANTEE_PTIA]: "Perte d'autonomie (PTIA)",
    [GUARANTEE_IPT_ITT]: 'ITT & IPT',
    [GUARANTEE_IPP]: 'Invalidité permanente partielle (IPP)',
    [GUARANTEE_PLUS]: 'Dos & psy',
}

export const GUARANTEE_COMPACT_DESCRIPTIONS: Record<GuaranteeCode, string> = {
    [GUARANTEE_DC]: 'Le capital restant dû est remboursé à la banque.',
    [GUARANTEE_PTIA]: 'Le prêt est soldé à votre place.',
    [GUARANTEE_IPT_ITT]: 'Mensualités prises en charge en arrêt de travail ou invalidité totale.',
    [GUARANTEE_IPP]: 'Couvre une invalidité reconnue entre 33 % et 66 %.',
    [GUARANTEE_PLUS]: "Étend l'ITT aux pathologies dorsales et psychiatriques.",
}

// Default required guarantees (DC + PTIA + IPT_ITT for residential)
export const DEFAULT_REQUIRED_GUARANTEES = [GUARANTEE_DC, GUARANTEE_PTIA, GUARANTEE_IPT_ITT]

// Guarantees required for different project purposes
export const GUARANTEES_BY_PROJECT_PURPOSE: Record<number, GuaranteeCode[]> = {
    [PROJECT_PURPOSE_RESIDENTIAL]: [GUARANTEE_DC, GUARANTEE_PTIA, GUARANTEE_IPT_ITT],
    [PROJECT_PURPOSE_RENTAL]: [GUARANTEE_DC, GUARANTEE_PTIA], // Investissement locatif
    [PROJECT_PURPOSE_SCPI]: [GUARANTEE_DC, GUARANTEE_PTIA], // SCPI
    [PROJECT_PURPOSE_PROFESSIONAL]: [GUARANTEE_DC, GUARANTEE_PTIA], // Prêt pro
}
